import { CounterSignatureLocation, RadioButtonOption, SignatureType } from "../../../../common/enums";
import { FormState_PopulateErrorMessages } from "../../../../common/formStateUtils";
import { CounterSignatureRequest_empty } from "../../../CounterSignatureRequest";
import Measures from "../../../permits/conditions/Measures";
import Errors from "../../../responses/Errors";
import { Signature_empty } from "../../../Signature";
import FormElement, { formElement } from "../../FormElement";
import ElectricDisconnectionChecklistFormState from "./ElectricDisconnectionChecklistFormState";
import ElectricFenceChecklistFormState from "./ElectricFenceChecklistFormState";
import Take2ChecklistFormState from "./Take2ChecklistFormState";
import WorkOnRoadChecklistFormState from "./WorkOnRoadChecklistFormState";
import ConstructionPowerChecklistFormState from "./ConstructionPowerChecklistFormState";

type MeasuresFormState = {
    gasFreeDeclaration: FormElement<boolean>;
    processEquipmentCleanedAndPressureFree: FormElement<boolean>;
    demarcationMeters: FormElement<number | undefined>;
    demarcationMetersChecked: FormElement<boolean>;
    electricalDisconnection: FormElement<boolean>;
    electricDisconnectionChecklist: ElectricDisconnectionChecklistFormState;
    electricFenceOnOff: FormElement<boolean>;
    electricFenceChecklist: ElectricFenceChecklistFormState;
    guardsPerTeamChecked: FormElement<boolean>;
    guardType: FormElement<string | undefined>;
    numberOfGuardsPerTeam: FormElement<number | undefined>;
    fireGuardsPerTeamChecked: FormElement<boolean>;
    numberOfFireGuardsPerTeam: FormElement<number | undefined>;
    lineGuardWillBeInformed: FormElement<boolean>;
    take2: FormElement<boolean>;
    take2Checklist: Take2ChecklistFormState;
    workOnRoad: FormElement<boolean>;
    workOnRoadChecklist: WorkOnRoadChecklistFormState;
    constructionPower: FormElement<boolean>;
    constructionPowerChecklist: ConstructionPowerChecklistFormState;
    isFlangeOpeningIncludedInPrint: FormElement<boolean>;
    otherInformation: FormElement<string | undefined>;
    interventionOnProcessEquipment: FormElement<boolean>;
};

export default MeasuresFormState;

export function MeasuresFormState_fromFormState(measures: MeasuresFormState): Measures {
    const electricalDisconnectionChecklist = measures.electricDisconnectionChecklist;
    const electricFenceChecklist = measures.electricFenceChecklist;
    const take2Checklist = measures.take2Checklist;
    const workOnRoadChecklist = measures.workOnRoadChecklist;
    const constructionPowerChecklist = measures.constructionPowerChecklist;

    return {
        demarcationMeters: measures.demarcationMeters.value,
        demarcationMetersChecked: measures.demarcationMetersChecked.value,
        electricalDisconnection: measures.electricalDisconnection.value,
        electricDisconnectionChecklist: {
            checklistVersion: undefined,
            cablesAreWithoutRiskOfFalling: electricalDisconnectionChecklist.cablesAreWithoutRiskOfFalling.value,
            electricConnectionsAreAboveGround: electricalDisconnectionChecklist.electricConnectionsAreAboveGround.value,
            equipmentIsConnectedToEarthFaultBreaker:
                electricalDisconnectionChecklist.equipmentIsConnectedToEarthFaultBreaker.value,
            powerIsCutAndFusesRemoved: electricalDisconnectionChecklist.powerIsCutAndFusesRemoved.value,
            responsibleElectricianName: electricalDisconnectionChecklist.responsibleElectricianName.value ?? "",
            responsibleElectricianPhoneNumber:
                electricalDisconnectionChecklist.responsibleElectricianPhoneNumber.value ?? "",
            signIsAtElectricalBreaker: electricalDisconnectionChecklist.signIsAtElectricalBreaker.value
        },
        electricFenceChecklist: {
            checklistVersion: undefined,
            checkedPowerIsOn: electricFenceChecklist.checkedPowerIsOn.value,
            controlledNoPersonAtFence: electricFenceChecklist.controlledNoPersonAtFence.value,
            highVoltageIsGrounded: electricFenceChecklist.highVoltageIsGrounded.value,
            lowHighVoltageIsCut: electricFenceChecklist.lowHighVoltageIsCut.value,
            portEntryIsInformedOfWork: electricFenceChecklist.portEntryIsInformedOfWork.value,
            powerOffIsControlled: electricFenceChecklist.powerOffIsControlled.value,
            securityCoordinatorInformedPermitReturned:
                electricFenceChecklist.securityCoordinatorInformedPermitReturned.value,
            securityCoordinatorInformedPermitUpdated:
                electricFenceChecklist.securityCoordinatorInformedPermitUpdated.value,
            securityRoundPerformed: electricFenceChecklist.securityRoundPerformed.value,
            workInProgressSignIsUp: electricFenceChecklist.workInProgressSignIsUp.value,
            signIsAtBreakerOrFuse: electricFenceChecklist.signIsAtBreakerOrFuse?.value ?? RadioButtonOption.None,
            powerOffWillBeControlled: electricFenceChecklist.powerOffWillBeControlled?.value ?? RadioButtonOption.None,
            securityCoordinatorInformedAboutStartedWork:
                electricFenceChecklist.securityCoordinatorInformedAboutStartedWork?.value ?? RadioButtonOption.None,
            electricianInformedPortEntryWorkComplete:
                electricFenceChecklist.electricianInformedPortEntryWorkComplete?.value ?? RadioButtonOption.None,
            securityCoordinatorInformedWorkComplete:
                electricFenceChecklist.securityCoordinatorInformedWorkComplete?.value ?? RadioButtonOption.None,

            powerOffDirectorFlammableProductSignature: Signature_empty(SignatureType.PowerOffDirectorFlammableProduct),
            powerOffExternalDirectorFlammableProductSignature: Signature_empty(
                SignatureType.PowerOffExternalDirectorFlammableProduct
            ),
            powerOffResponsibleElectricianSignature: Signature_empty(SignatureType.PowerOffResponsibleElectrician),
            powerOffWorkLeaderSignature: Signature_empty(SignatureType.PowerOffWorkLeader),
            powerOffCounterSignatureRequest: CounterSignatureRequest_empty(
                CounterSignatureLocation.ElectricFencePowerOff
            ),

            powerOnDirectorFlammableProductSignature: Signature_empty(SignatureType.PowerOnDirectorFlammableProduct),
            powerOnExternalDirectorFlammableProductSignature: Signature_empty(
                SignatureType.PowerOnExternalDirectorFlammableProduct
            ),
            powerOnResponsibleElectricianSignature: Signature_empty(SignatureType.PowerOnResponsibleElectrician),
            powerOnWorkLeaderSignature: Signature_empty(SignatureType.PowerOnWorkLeader),
            powerOffPortOfficerSignature: Signature_empty(SignatureType.PowerOffPortOfficer),
            powerOnPortOfficerSignature: Signature_empty(SignatureType.PowerOnPortOfficer),
            powerOnCounterSignatureRequest: CounterSignatureRequest_empty(CounterSignatureLocation.ElectricFencePowerOn)
        },
        electricFenceOnOff: measures.electricFenceOnOff.value,
        fireGuardsPerTeamChecked: measures.fireGuardsPerTeamChecked.value,
        gasFreeDeclaration: measures.gasFreeDeclaration.value,
        guardsPerTeamChecked: measures.guardsPerTeamChecked.value,
        guardType: measures.guardType.value ?? "",
        interventionOnProcessEquipment: measures.interventionOnProcessEquipment.value,
        isFlangeOpeningIncludedInPrint: measures.isFlangeOpeningIncludedInPrint.value,
        lineGuardWillBeInformed: measures.lineGuardWillBeInformed.value,
        numberOfFireGuardsPerTeam: measures.numberOfFireGuardsPerTeam.value,
        numberOfGuardsPerTeam: measures.numberOfGuardsPerTeam.value,
        otherInformation: measures.otherInformation.value ?? "",
        processEquipmentCleanedAndPressureFree: measures.processEquipmentCleanedAndPressureFree.value,
        take2: measures.take2.value,
        take2Checklist: {
            checklistVersion: undefined,
            dutySwitchIsDisconnected: take2Checklist.dutySwitchIsDisconnected.value,
            equipmentIsDrainedAndNoPreassure: take2Checklist.equipmentIsDrainedAndNoPreassure.value,
            hasAppropriateScaffolding: take2Checklist.hasAppropriateScaffolding.value,
            haveEquipment: take2Checklist.haveEquipment.value,
            haveProtectionEquipment: take2Checklist.haveProtectionEquipment.value,
            haveReadPermitAndTerms: take2Checklist.haveReadPermitAndTerms.value,
            haveUnderstoodInstructions: take2Checklist.haveUnderstoodInstructions.value,
            isNearbyWork: take2Checklist.isNearbyWork.value,
            needsBlockingOff: take2Checklist.needsBlockingOff.value,
            rightPlaceAndEquipment: take2Checklist.rightPlaceAndEquipment.value,
            riskObservationCauseDescription: take2Checklist.riskObservationCauseDescription.value,
            riskObservationCourseOfEvents: take2Checklist.riskObservationCourseOfEvents.value,
            riskObservationDateTime: take2Checklist.riskObservationDateTime.value,
            riskObservationFixSuggestion: take2Checklist.riskObservationFixSuggestion.value,
            riskObservationLocation: take2Checklist.riskObservationLocation.value,
            riskObservationNotifier: take2Checklist.riskObservationNotifier.value,
            riskOfBurnsCrushCuts: take2Checklist.riskOfBurnsCrushCuts.value,
            riskOfFallsOrFallingObjects: take2Checklist.riskOfFallsOrFallingObjects.value,
            riskOfFire: take2Checklist.riskOfFire.value,
            whatRTO: take2Checklist.whatRTO.value,
            whereIsClosestAlarmEquipmentDescription: take2Checklist.whereIsClosestAlarmEquipmentDescription.value,
            whereIsClosestEyeWashAndShowerDescription: take2Checklist.whereIsClosestEyeWashAndShowerDescription.value,
            whereIsClosestExtinguishingEquipmentDescription:
                take2Checklist.whereIsClosestExtinguishingEquipmentDescription.value,
            whichProcessEquipmentMediaDescription: take2Checklist.whichProcessEquipmentMediaDescription.value,
            knowWhereClosestAlarmEquipmentIs: take2Checklist.knowWhereClosestAlarmEquipmentIs?.value ?? RadioButtonOption.None,
            haveKnowledgeOfProcessEquipmentMedia: take2Checklist.haveKnowledgeOfProcessEquipmentMedia?.value ?? RadioButtonOption.None,
            needsWorkplaceBlockingOff: take2Checklist.needsWorkplaceBlockingOff?.value ?? RadioButtonOption.None,
            knowWhereClosestEyeWashAndShowerIs: take2Checklist.knowWhereClosestEyeWashAndShowerIs?.value ?? RadioButtonOption.None,
            hasFireRisk: take2Checklist.hasFireRisk?.value ?? RadioButtonOption.None,
            knowWhereClosestExtinguishingEquipmentIs: take2Checklist.knowWhereClosestExtinguishingEquipmentIs?.value ?? RadioButtonOption.None
        },
        workOnRoad: measures.workOnRoad.value,
        workOnRoadChecklist: {
            checklistVersion: undefined,
            hasTAPlan: workOnRoadChecklist.hasTAPlan.value,
            hasAtLeastTwoTrafficSignBoards: workOnRoadChecklist.hasAtLeastTwoTrafficSignBoards.value,
            allWorkersHaveCorrectRoadWorkTraining: workOnRoadChecklist.allWorkersHaveCorrectRoadWorkTraining.value,
            allWorkersHaveCorrectProtectiveEquipment:
                workOnRoadChecklist.allWorkersHaveCorrectProtectiveEquipment.value,
            canUnprotectedIndividualsPassSafely: workOnRoadChecklist.canUnprotectedIndividualsPassSafely.value
        },
        constructionPower: measures.constructionPower.value,
        constructionPowerChecklist: {
            checklistVersion: undefined,
            willCableBePlacedOnBareGround: constructionPowerChecklist.willCableBePlacedOnBareGround.value,
            allJunctionsInTrayOrSuspendedAboveGround:
                constructionPowerChecklist.allJunctionsInTrayOrSuspendedAboveGround.value,
            cablesAreWithoutTripHazard: constructionPowerChecklist.cablesAreWithoutTripHazard.value,
            cableIsInProtectionTube: constructionPowerChecklist.cableIsInProtectionTube.value,
            cableGoesOverRoadCycleOrWalkway: constructionPowerChecklist.cableGoesOverRoadCycleOrWalkway.value,
            isCableProtectedWithRunOverProtection:
                constructionPowerChecklist.isCableProtectedWithRunOverProtection.value,
            haveSignsWarningForCableOverRoad: constructionPowerChecklist.haveSignsWarningForCableOverRoad.value
        }
    };
}

export function MeasuresFormState_default(): MeasuresFormState {
    return {
        demarcationMeters: formElement(undefined),
        demarcationMetersChecked: formElement(false),
        electricalDisconnection: formElement(false),
        electricDisconnectionChecklist: {
            cablesAreWithoutRiskOfFalling: formElement(RadioButtonOption.None),
            electricConnectionsAreAboveGround: formElement(RadioButtonOption.None),
            equipmentIsConnectedToEarthFaultBreaker: formElement(RadioButtonOption.None),
            powerIsCutAndFusesRemoved: formElement(RadioButtonOption.None),
            responsibleElectricianName: formElement(""),
            responsibleElectricianPhoneNumber: formElement(""),
            signIsAtElectricalBreaker: formElement(RadioButtonOption.None)
        },
        electricFenceChecklist: {
            checkedPowerIsOn: formElement(RadioButtonOption.None),
            controlledNoPersonAtFence: formElement(RadioButtonOption.None),
            highVoltageIsGrounded: formElement(RadioButtonOption.None),
            lowHighVoltageIsCut: formElement(RadioButtonOption.None),
            portEntryIsInformedOfWork: formElement(RadioButtonOption.None),
            powerOffIsControlled: formElement(RadioButtonOption.None),
            securityCoordinatorInformedPermitReturned: formElement(RadioButtonOption.None),
            securityCoordinatorInformedPermitUpdated: formElement(RadioButtonOption.None),
            securityRoundPerformed: formElement(RadioButtonOption.None),
            workInProgressSignIsUp: formElement(RadioButtonOption.None),
            signIsAtBreakerOrFuse: formElement(RadioButtonOption.None),
            powerOffWillBeControlled: formElement(RadioButtonOption.None),
            securityCoordinatorInformedAboutStartedWork: formElement(RadioButtonOption.None),
            electricianInformedPortEntryWorkComplete: formElement(RadioButtonOption.None),
            securityCoordinatorInformedWorkComplete: formElement(RadioButtonOption.None),
            powerOffDirectorFlammableProductSignature: Signature_empty(SignatureType.PowerOffDirectorFlammableProduct),
            powerOffExternalDirectorFlammableProductSignature: Signature_empty(
                SignatureType.PowerOffExternalDirectorFlammableProduct
            ),
            powerOffResponsibleElectricianSignature: Signature_empty(SignatureType.PowerOffResponsibleElectrician),
            powerOffWorkLeaderSignature: Signature_empty(SignatureType.PowerOffWorkLeader),

            powerOnDirectorFlammableProductSignature: Signature_empty(SignatureType.PowerOnDirectorFlammableProduct),
            powerOnExternalDirectorFlammableProductSignature: Signature_empty(
                SignatureType.PowerOnExternalDirectorFlammableProduct
            ),
            powerOnResponsibleElectricianSignature: Signature_empty(SignatureType.PowerOnResponsibleElectrician),
            powerOnWorkLeaderSignature: Signature_empty(SignatureType.PowerOnWorkLeader)
        },
        electricFenceOnOff: formElement(false),
        fireGuardsPerTeamChecked: formElement(false),
        gasFreeDeclaration: formElement(false),
        guardsPerTeamChecked: formElement(false),
        guardType: formElement(""),
        interventionOnProcessEquipment: formElement(false),
        isFlangeOpeningIncludedInPrint: formElement(false),
        lineGuardWillBeInformed: formElement(false),
        numberOfFireGuardsPerTeam: formElement(undefined),
        numberOfGuardsPerTeam: formElement(undefined),
        otherInformation: formElement(""),
        processEquipmentCleanedAndPressureFree: formElement(false),
        take2: formElement(true),
        take2Checklist: {
            dutySwitchIsDisconnected: formElement(RadioButtonOption.None),
            equipmentIsDrainedAndNoPreassure: formElement(RadioButtonOption.None),
            hasAppropriateScaffolding: formElement(RadioButtonOption.None),
            haveEquipment: formElement(RadioButtonOption.None),
            haveProtectionEquipment: formElement(RadioButtonOption.None),
            haveReadPermitAndTerms: formElement(RadioButtonOption.None),
            haveUnderstoodInstructions: formElement(RadioButtonOption.None),
            isNearbyWork: formElement(RadioButtonOption.None),
            needsBlockingOff: formElement(RadioButtonOption.None),
            rightPlaceAndEquipment: formElement(RadioButtonOption.None),
            riskObservationCauseDescription: formElement(""),
            riskObservationCourseOfEvents: formElement(""),
            riskObservationDateTime: formElement(""),
            riskObservationFixSuggestion: formElement(""),
            riskObservationLocation: formElement(""),
            riskObservationNotifier: formElement(""),
            riskOfBurnsCrushCuts: formElement(RadioButtonOption.None),
            riskOfFallsOrFallingObjects: formElement(RadioButtonOption.None),
            riskOfFire: formElement(RadioButtonOption.None),
            whatRTO: formElement(""),
            whereIsClosestAlarmEquipmentDescription: formElement(""),
            whereIsClosestEyeWashAndShowerDescription: formElement(""),
            whereIsClosestExtinguishingEquipmentDescription: formElement(""),
            whichProcessEquipmentMediaDescription: formElement(""),
            hasFireRisk: formElement(RadioButtonOption.None),
            knowWhereClosestAlarmEquipmentIs: formElement(RadioButtonOption.None),
            haveKnowledgeOfProcessEquipmentMedia: formElement(RadioButtonOption.None),
            needsWorkplaceBlockingOff: formElement(RadioButtonOption.None),
            knowWhereClosestEyeWashAndShowerIs: formElement(RadioButtonOption.None),
            knowWhereClosestExtinguishingEquipmentIs: formElement(RadioButtonOption.None)
        },
        workOnRoad: formElement(false),
        workOnRoadChecklist: {
            hasTAPlan: formElement(RadioButtonOption.None),
            hasAtLeastTwoTrafficSignBoards: formElement(RadioButtonOption.None),
            allWorkersHaveCorrectRoadWorkTraining: formElement(RadioButtonOption.None),
            allWorkersHaveCorrectProtectiveEquipment: formElement(RadioButtonOption.None),
            canUnprotectedIndividualsPassSafely: formElement(RadioButtonOption.None)
        },
        constructionPower: formElement(false),
        constructionPowerChecklist: {
            willCableBePlacedOnBareGround: formElement(RadioButtonOption.None),
            allJunctionsInTrayOrSuspendedAboveGround: formElement(RadioButtonOption.None),
            cablesAreWithoutTripHazard: formElement(RadioButtonOption.None),
            cableIsInProtectionTube: formElement(RadioButtonOption.None),
            cableGoesOverRoadCycleOrWalkway: formElement(RadioButtonOption.None),
            isCableProtectedWithRunOverProtection: formElement(RadioButtonOption.None),
            haveSignsWarningForCableOverRoad: formElement(RadioButtonOption.None)
        }
    };
}

export function MeasuresFormState_populateErrorMessages(
    formState: MeasuresFormState,
    errors: Errors
): MeasuresFormState {
    const updatedFormState = FormState_PopulateErrorMessages(formState, errors);

    return updatedFormState;
}
